import { useCallback, useRef, useState } from "react"
import { Linking } from "react-native"
import * as amplitude from "@amplitude/analytics-react-native"
import { IS_WEB } from "@common/utils/mobileUtils"
import { subscriptionService } from "../../../data/services/subscriptionService"
import { apiError } from "../newSubscriptionUtils"

/** Le plus qu'on attend Amplitude avant de quitter la page pour Stripe. */
const FLUSH_BEFORE_LEAVING_MS = 1000

/**
 * « Payer sur la page sécurisée Stripe » : ouvre la même vente sur la page de
 * Stripe, pour la famille dont le navigateur empêche notre page de monter son
 * formulaire de paiement (bloqueur de contenu). Le serveur y met les mêmes
 * montant et métadonnées, et Stripe ramène la famille sur le choix des créneaux,
 * comme après un paiement dans la page.
 *
 * `openHostedCheckout(trigger)` : `trigger` dit ce qui a mené au repli (échec
 * détecté du formulaire, paiement sans réponse, ou choix de la famille).
 */
export const useHostedCheckoutFallback = ({ email, surface }) => {
    const [isOpening, setIsOpening] = useState(false)
    const [error, setError] = useState(null)
    // Un second clic pendant l'ouverture créerait une autre session Stripe.
    const openingRef = useRef(false)

    const openHostedCheckout = useCallback(
        async (trigger) => {
            if (openingRef.current) return
            openingRef.current = true
            setIsOpening(true)
            setError(null)
            amplitude.track("checkout.hosted_fallback_opened", {
                surface,
                trigger,
            })
            // Amplitude envoie par lots : lancé maintenant, l'envoi court pendant
            // l'appel au serveur ; sans lui, l'événement partait avec la page.
            const flushed = amplitude.flush().promise

            try {
                const url = (
                    await subscriptionService.adminCreateHostedCheckoutSession(
                        {},
                        { email }
                    )
                ).data?.data?.url
                if (!url) throw new Error("no hosted checkout url")

                await Promise.race([
                    flushed,
                    new Promise((resolve) =>
                        setTimeout(resolve, FLUSH_BEFORE_LEAVING_MS)
                    ),
                ])

                // On quitte la page : le bouton reste en chargement jusqu'au départ.
                if (IS_WEB) window.location.assign(url)
                else await Linking.openURL(url)
            } catch (err) {
                openingRef.current = false
                setIsOpening(false)
                setError(
                    apiError(
                        err,
                        "La page de paiement Stripe n'a pas pu s'ouvrir. Réessayez dans un instant."
                    )
                )
            }
        },
        [email, surface]
    )

    return { openHostedCheckout, isOpening, error }
}
